import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './Subscribe.css';

const BASE_PRICING = {
  INR: {
    currency: 'INR',
    symbol: '₹',
    monthly: { amount: 29900, label: '₹299', desc: '₹299/month' },
    quarterly: { amount: 79900, label: '₹799', desc: '₹799/3 months', savings: 'Save 11%' },
  },
  USD: {
    currency: 'USD',
    symbol: '$',
    monthly: { amount: 999, label: '$9.99', desc: '$9.99/month' },
    quarterly: { amount: 2499, label: '$24.99', desc: '$24.99/3 months', savings: 'Save 17%' },
  },
};

function Subscribe() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, refreshProfile, isAdmin } = useAuth();
  const [selected, setSelected] = useState('quarterly');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [currency, setCurrency] = useState('USD');
  const [pricing, setPricing] = useState(BASE_PRICING.USD);
  const [country, setCountry] = useState('');
  const [checkingLocation, setCheckingLocation] = useState(true);

  useEffect(() => {
    if (searchParams.get('success')) {
      setSuccess(true);
      refreshProfile();
      setTimeout(() => navigate('/browse'), 2000);
      return;
    }
    detectCountry();
  }, []);

  const detectCountry = async () => {
    try {
      const res = await fetch('https://api.country.is/');
      const data = await res.json();
      if (data.country === 'IN') {
        setCountry('India');
        setCurrency('INR');
        setPricing(BASE_PRICING.INR);
      } else {
        setCountry(data.country || '');
        setCurrency('USD');
        setPricing(BASE_PRICING.USD);
      }
    } catch (err) {
      console.error('IP detect failed:', err);
      setCurrency('USD');
      setPricing(BASE_PRICING.USD);
    } finally {
      setCheckingLocation(false);
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) { resolve(true); return; }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        alert('Failed to load payment gateway. Please try again.');
        setLoading(false);
        return;
      }

      const orderRes = await axios.post('/api/auth/create-order', {
        plan: selected,
        currency,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const { order_id, amount, key_id, user_name, user_email } = orderRes.data;

      const options = {
        key: key_id,
        amount,
        currency,
        name: 'SponsorBoat',
        description: selected === 'monthly' ? 'Monthly Subscription' : 'Quarterly Subscription',
        order_id,
        prefill: { name: user_name, email: user_email },
        theme: { color: '#667eea' },
        handler: async function (response) {
          try {
            await axios.post('/api/auth/verify-payment', {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
              plan: selected,
              currency,
            }, {
              headers: { Authorization: `Bearer ${token}` },
            });
            await refreshProfile();
            setSuccess(true);
            setTimeout(() => navigate('/browse'), 2000);
          } catch {
            alert('Payment verified but subscription activation failed. Contact support.');
          }
        },
        modal: {
          ondismiss: () => setLoading(false),
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', () => {
        alert('Payment failed. Please try again.');
        setLoading(false);
      });
      rzp.open();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to initiate payment';
      alert(msg);
      setLoading(false);
    }
  };

  const isSubscribed = (user?.subscription_end && new Date(user.subscription_end) > new Date()) || isAdmin();

  if (success || isSubscribed) {
    return (
      <div className="subscribe-page">
        <div className="subscribe-success">
          <h2>Subscription Active!</h2>
          <p>You have full access to browse all creator posts.</p>
          <button className="subscribe-cta" onClick={() => navigate('/browse')}>
            Start Browsing
          </button>
        </div>
      </div>
    );
  }

  if (checkingLocation) {
    return (
      <div className="subscribe-page">
        <div className="subscribe-success" style={{ background: 'transparent', boxShadow: 'none' }}>
          <h2>Loading Plans...</h2>
          <p className="loading-text">Optimizing pricing for your region...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="subscribe-page">
        <div className="subscribe-success">
          <h2>Account Required</h2>
          <p>Please sign up or log in to subscribe.</p>
          <button className="subscribe-cta" onClick={() => navigate('/signup')}>
            Sign Up
          </button>
        </div>
      </div>
    );
  }

  const p = pricing;

  return (
    <div className="subscribe-page">
      <div className="subscribe-header">
        <h1>Choose Your Plan</h1>
        <p>Get access to browse YouTube channels, Instagram accounts, and blogs looking for sponsorship</p>
      </div>
      <div className="subscribe-plans">
        <div
          className={`subscribe-card ${selected === 'monthly' ? 'selected' : ''}`}
          onClick={() => setSelected('monthly')}
        >
          <h3>Monthly</h3>
          <div className="plan-price">
            <span className="price">{p.monthly.label}</span>
            <span className="period">/month</span>
          </div>
          <p className="plan-desc">{p.monthly.desc}</p>
          <ul className="plan-features">
            <li>Browse all creator posts</li>
            <li>View contact details</li>
            <li>Cancel anytime</li>
          </ul>
          <button
            className={`plan-btn ${selected === 'monthly' ? 'active' : ''}`}
            onClick={(e) => { e.stopPropagation(); setSelected('monthly'); }}
          >
            {selected === 'monthly' ? 'Selected' : 'Choose'}
          </button>
        </div>
        <div
          className={`subscribe-card recommended ${selected === 'quarterly' ? 'selected' : ''}`}
          onClick={() => setSelected('quarterly')}
        >
          <span className="plan-badge">Recommended</span>
          <h3>Quarterly</h3>
          <div className="plan-price">
            <span className="price">{p.quarterly.label}</span>
            <span className="period">/3 months</span>
          </div>
          <p className="plan-desc">{p.quarterly.desc}</p>
          {p.quarterly.savings && <span className="plan-savings">{p.quarterly.savings}</span>}
          <ul className="plan-features">
            <li>Everything in Monthly</li>
            <li>3 months of access</li>
            <li>Best value</li>
          </ul>
          <button
            className={`plan-btn ${selected === 'quarterly' ? 'active' : ''}`}
            onClick={(e) => { e.stopPropagation(); setSelected('quarterly'); }}
          >
            {selected === 'quarterly' ? 'Selected' : 'Choose'}
          </button>
        </div>
      </div>
      <button className="subscribe-cta" onClick={handleSubscribe} disabled={loading}>
        {loading ? 'Opening payment...' : `Subscribe ${selected === 'monthly' ? p.monthly.label : p.quarterly.label}`}
      </button>
      <p className="subscribe-note">You can cancel your subscription at any time.</p>
    </div>
  );
}

export default Subscribe;
